// src/features/records/components/NoteView/RecordCard.js
import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styles from './RecordCard.module.css';
import RecordActions from '../shared/RecordActions';
import { Avatar, AvatarSkeleton } from '../../../../components/avatars';
import RecordTags from './RecordTags';
import RecordAttachments from '../shared/RecordAttachments';
import AttachmentPreviewStrip from '../shared/AttachmentPreviewStrip';
import CompactLinkedRecords from '../shared/CompactLinkedRecords';
import RecordDropdownMenu from '../shared/RecordDropdownMenu';
import ShareDialog from '../ShareDialog/ShareDialog';
import { formatTimeAgo } from '../../../../utils/dateUtils';
import { sanitizeText } from '../../../../utils/sanitize';

const RecordCard = ({ record, authorProfile, profileLoading, onLinkToRecord, onEdit, onDelete, onDetailView, observeRecord, unobserveRecord }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const unknownUserLabel = t('records.views.note.card.user_fallback_name', 'Unknown User');
  const authorDisplayName = sanitizeText(authorProfile?.displayName) || unknownUserLabel;
  const avatarAlt = authorProfile?.displayName
    ? t('records.views.note.card.user_avatar_alt', {
        name: authorProfile.displayName,
        defaultValue: '{{name}} avatar'
      })
    : t('records.views.note.card.user_avatar_fallback_alt', 'User avatar');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const cardRef = useRef(null);

  const handleCardClick = useCallback((e) => {
    if (showShareDialog || e.defaultPrevented) {
      return;
    }

    // Avoid triggering on button clicks
    if (e.target.closest('button') || e.target.closest(`.${styles.tag}`)) {
      return;
    }
    
    // Navigate to record detail view
    if (onDetailView) {
      onDetailView(record);
    }
  }, [record, onDetailView, showShareDialog]);

  const handleEdit = useCallback(() => {
    if (onEdit) {
      onEdit(record);
    }
  }, [record, onEdit]);

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete(record);
    }
  }, [record, onDelete]);

  const handleShare = useCallback(() => {
    setShowShareDialog(true);
  }, []);

  // Handle visibility tracking
  useEffect(() => {
    if (observeRecord && cardRef.current) {
      observeRecord(record.id, cardRef.current);
    }

    return () => {
      if (unobserveRecord) {
        unobserveRecord(record.id);
      }
    };
  }, [record.id, observeRecord, unobserveRecord]);

  // Get date from metadata
  const createdAt = record.metadata?.createdAt || record.createdAt || new Date();

  return (
    <>
      <article ref={cardRef} className={styles.recordCard} onClick={handleCardClick}>
      {/* Author Section */}
      {profileLoading && !authorProfile ? (
        <div className={styles.recordAuthor}>
          <AvatarSkeleton className={styles.avatarSkeleton} />
          <div className={styles.authorInfoSkeleton}>
            <div className={styles.nameSkeleton} />
            <div className={styles.metaSkeleton} />
          </div>
        </div>
      ) : (
        <div className={styles.recordAuthor}>
          <Avatar
            name={authorDisplayName}
            src={authorProfile?.profilePictureUrl}
            alt={avatarAlt}
          />
          <div className={styles.authorInfo}>
            <div className={styles.authorName}>{authorDisplayName}</div>
            <div className={styles.recordMeta}>
              <span>{formatTimeAgo(createdAt, { locale, t })}</span>
            </div>
          </div>
          <RecordDropdownMenu
            isOpen={showDropdown}
            onToggle={setShowDropdown}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onShare={handleShare}
            record={record}
          />
        </div>
      )}

      {/* Content */}
      <h2 className={styles.recordTitle}>
        {sanitizeText(record.content?.title) || t('records.views.note.card.untitled', 'Untitled')}
      </h2>
      <p className={styles.recordBody}>{sanitizeText(record.content?.body) || ''}</p>

      {/* Linked Records */}
      {record.links?.outgoing?.length > 0 && (
        <div className={styles.linkedRecordsSection}>
          <CompactLinkedRecords links={record.links.outgoing} />
        </div>
      )}

      {/* Attachments */}
      {record.content.attachments?.length > 0 ? (
        <RecordAttachments attachments={record.content.attachments} />
      ) : record.content.attachmentsSummary?.count > 0 ? (
        <AttachmentPreviewStrip 
          previews={record.content.attachmentsSummary.previews}
          count={record.content.attachmentsSummary.count}
        />
      ) : null}

      {/* Tags */}
      {record.content.tags?.length > 0 && (
        <RecordTags tags={record.content.tags} />
      )}

      {/* Actions */}
      <RecordActions
        recordId={record.id}
        onLinkToRecord={onLinkToRecord}
        onComment={() => onDetailView?.(record, { openComments: true })}
      />
    </article>

    {showShareDialog && (
      <ShareDialog
        record={record}
        onClose={() => setShowShareDialog(false)}
      />
    )}
    </>
  );
};

RecordCard.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.shape({
      title: PropTypes.string,
      body: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string),
      attachments: PropTypes.arrayOf(PropTypes.object),
      attachmentsSummary: PropTypes.shape({
        version: PropTypes.number,
        count: PropTypes.number,
        latestUploadedAt: PropTypes.object,
        previews: PropTypes.arrayOf(PropTypes.shape({
          id: PropTypes.string,
          fileType: PropTypes.string,
          thumbnail: PropTypes.string,
          uploadedAt: PropTypes.object
        }))
      })
    }),
    metadata: PropTypes.shape({
      createdAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
        PropTypes.object
      ]),
      updatedAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
        PropTypes.object
      ])
    }),
    access: PropTypes.shape({
      owner: PropTypes.string
    }),
    attributes: PropTypes.object,
    links: PropTypes.shape({
      outgoing: PropTypes.arrayOf(PropTypes.object)
    }),
  }).isRequired,
  authorProfile: PropTypes.shape({
    id: PropTypes.string,
    displayName: PropTypes.string,
    username: PropTypes.string,
    profilePictureUrl: PropTypes.string
  }),
  profileLoading: PropTypes.bool,
  onLinkToRecord: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onDetailView: PropTypes.func,
  observeRecord: PropTypes.func,
  unobserveRecord: PropTypes.func
};

export default React.memo(RecordCard);
